import { useMemo, useState } from 'react';
import {
  Accordion,
  ActionIcon,
  Avatar,
  Box,
  Divider,
  Group,
  Menu,
  Text,
  UnstyledButton,
} from '@mantine/core';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../stores/authStore';
import { useLayoutStore } from '../../stores/layoutStore';
import { useResponsiveLayout } from '../../hooks/useResponsiveLayout';
import type { District, Field } from '../../types';
import type { Farm } from '../../types/farm';
import type { Region, SubRegion } from '../../types/region';
import { Role } from '../../types/user';
import { MANUAL_PDF_URL, WEATHER_RISK_CRITERIA_PDF_URL } from '../../constants/externalLinks';
import { colors } from '../../theme/colors';
import { layout } from '../../theme/layout';
import { MaterialIcon } from '../common/MaterialIcon';
import { ChangePasswordModal } from '../common/ChangePasswordModal';
import { DebugModal } from '../common/DebugModal';
import { formatFieldArea } from '../../utils/fieldUtils';

interface LeftPanelProps {
  fields: Field[];
  farms: Farm[];
  areas: District[];
  regions: Region[];
  subRegions: SubRegion[];
  role: Role | undefined;
  selectedFieldId: string | null;
  selectedFarmName?: string | null;
  selectedDistrictName?: string | null;
  onFieldSelect: (fieldId: string) => void;
  onFieldHover?: (fieldId: string | null) => void;
  onDistrictSelect?: (districtId: string) => void;
  onFarmSelect?: (districtId: string, farmId: string) => void;
}

interface FarmGroup {
  id: string;
  name: string;
  fields: Field[];
}

interface AreaGroup {
  district: District;
  farmGroups: FarmGroup[];
}

/**
 * 左サイドバーコンポーネント
 * ロール別に異なる階層構造を表示する:
 * - ADMIN: Region > SubRegion > Area > Farm > Field
 * - AREA_MANAGER: Area > Farm > Field
 * - FARMER: Farm > Field
 */
export function LeftPanel({
  fields,
  areas,
  regions,
  subRegions,
  role,
  selectedFieldId,
  selectedFarmName,
  selectedDistrictName,
  onFieldSelect,
  onFieldHover,
  onDistrictSelect,
  onFarmSelect,
}: LeftPanelProps) {
  const { user, logout } = useAuthStore();
  const { closeDrawer, districtAccordionValue, setDistrictAccordionValue } = useLayoutStore();
  const { showInlineSidebar } = useResponsiveLayout();
  const navigate = useNavigate();
  const [changePasswordOpened, setChangePasswordOpened] = useState(false);
  const [debugModalOpened, setDebugModalOpened] = useState(false);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('ログアウトエラー:', error);
    }
  };

  const getUserInitials = () => {
    if (user?.name) {
      return user.name.slice(0, 2);
    }
    if (user?.email) {
      return user.email[0].toUpperCase();
    }
    return 'U';
  };

  // Area ごとに Farm > Field をグルーピング
  const areaGroups = useMemo((): AreaGroup[] => {
    return areas.map((area) => {
      const areaFields = fields.filter((f) => f.district.id === area.id && f.name !== undefined);
      const farmMap = new Map<string, FarmGroup>();
      for (const field of areaFields) {
        if (field.farmId && field.farmName) {
          if (!farmMap.has(field.farmId)) {
            farmMap.set(field.farmId, { id: field.farmId, name: field.farmName, fields: [] });
          }
          farmMap.get(field.farmId)!.fields.push(field);
        }
      }
      return { district: area, farmGroups: [...farmMap.values()] };
    });
  }, [fields, areas]);

  // Farm > Field のレンダリング（共通部品）
  const renderFarmAccordion = (farmGroups: FarmGroup[], districtId: string) => (
    <Accordion
      multiple
      chevronPosition="left"
      variant="subtle"
      value={districtAccordionValue}
      onChange={setDistrictAccordionValue}
    >
      {farmGroups.map((farm) => (
        <Accordion.Item key={`farm-${farm.id}`} value={`farm-${farm.id}`}>
          <Accordion.Control>
            <Box
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                onFarmSelect?.(districtId, farm.id);
              }}
              style={{
                padding: layout.spacing.xs,
                borderRadius: layout.border.radius,
                backgroundColor:
                  selectedFarmName === farm.name ? colors.selection.background : 'transparent',
                cursor: 'pointer',
              }}
            >
              <Text
                size="sm"
                fw={500}
                c={selectedFarmName === farm.name ? colors.selection.text : colors.gray[900]}
              >
                {farm.name}
              </Text>
            </Box>
          </Accordion.Control>
          <Accordion.Panel>
            {farm.fields.map((field) => (
              <UnstyledButton
                key={field.id}
                onClick={() => onFieldSelect(field.id)}
                onMouseEnter={() => onFieldHover?.(field.id)}
                onMouseLeave={() => onFieldHover?.(null)}
                style={{
                  width: '100%',
                  padding: layout.spacing.sm,
                  display: 'flex',
                  alignItems: 'center',
                  gap: layout.spacing.sm,
                  backgroundColor:
                    selectedFieldId === field.id ? colors.selection.background : 'transparent',
                  borderRadius: layout.border.radius,
                  cursor: 'pointer',
                }}
              >
                <MaterialIcon icon="place" size="sm" color={colors.primary} />
                <Text
                  size="sm"
                  c={selectedFieldId === field.id ? colors.selection.text : colors.gray[900]}
                >
                  {field.name}
                </Text>
                <Text size="xs" c={colors.gray[600]} style={{ marginLeft: 'auto' }}>
                  {formatFieldArea(field)}
                </Text>
              </UnstyledButton>
            ))}
          </Accordion.Panel>
        </Accordion.Item>
      ))}
    </Accordion>
  );

  // Area セクション（クリック可能なヘッダー + Farm ツリー）
  const renderAreaSection = (areaGroup: AreaGroup) => (
    <Box key={`area-${areaGroup.district.id}`} mb="xs">
      <UnstyledButton
        onClick={() => onDistrictSelect?.(areaGroup.district.id)}
        style={{
          width: '100%',
          padding: `${layout.spacing.xs} ${layout.spacing.sm}`,
          borderRadius: layout.border.radius,
          backgroundColor:
            selectedDistrictName === areaGroup.district.name && !selectedFarmName
              ? colors.selection.background
              : 'transparent',
        }}
      >
        <Text
          size="sm"
          fw={600}
          c={
            selectedDistrictName === areaGroup.district.name && !selectedFarmName
              ? colors.selection.text
              : colors.gray[800]
          }
        >
          {areaGroup.district.name}
        </Text>
      </UnstyledButton>
      {areaGroup.farmGroups.length > 0 && (
        <Box pl="sm">{renderFarmAccordion(areaGroup.farmGroups, areaGroup.district.id)}</Box>
      )}
    </Box>
  );

  // ADMIN: Region > SubRegion > Area > Farm > Field
  const renderAdminTree = () => (
    <>
      {regions.map((region) => {
        const regionSubRegions = subRegions.filter((sr) => sr.regionId === region.id);
        return (
          <Box key={`region-${region.id}`} mb="md">
            <Text size="xs" fw={700} tt="uppercase" c="dimmed" px="sm" mb={4}>
              {region.name}
            </Text>
            {regionSubRegions.map((sr) => {
              const srAreas = areaGroups.filter((ag) => ag.district.subRegionId === sr.id);

              if (srAreas.length === 0) return null;

              return (
                <Box key={`sr-${sr.id}`} mb="sm">
                  <Divider
                    label={
                      <Text size="xs" fw={600} c="dimmed">
                        {sr.name}
                      </Text>
                    }
                    labelPosition="left"
                    mx="sm"
                    mb={4}
                  />
                  <Box pl="xs">{srAreas.map(renderAreaSection)}</Box>
                </Box>
              );
            })}
          </Box>
        );
      })}
    </>
  );

  // AREA_MANAGER: Area > Farm > Field
  const renderAreaManagerTree = () => <>{areaGroups.map(renderAreaSection)}</>;

  // FARMER: Farm > Field（Farm のみフラット表示）
  const renderFarmerTree = () => {
    const allFarmGroups: FarmGroup[] = [];
    for (const ag of areaGroups) {
      allFarmGroups.push(...ag.farmGroups);
    }
    if (allFarmGroups.length === 0) return null;
    return renderFarmAccordion(allFarmGroups, '');
  };

  return (
    <Box style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* アカウントメニュー */}
      <Box
        style={{
          padding: '0 0.5rem',
          height: `${layout.header.height}px`,
          minHeight: `${layout.header.height}px`,
          borderBottom: `1px solid ${colors.gray[200]}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        {user && (
          <Menu>
            <Menu.Target>
              <Group gap={4} style={{ cursor: 'pointer' }}>
                <Avatar color="blue" radius="xl" size="sm">
                  {getUserInitials()}
                </Avatar>
                <Text size="xs" c="dimmed">
                  {user.name}
                </Text>
                <MaterialIcon icon="expand_more" size="sm" color={colors.gray[600]} />
              </Group>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Label>{user.name || user.email}</Menu.Label>
              <Menu.Divider />
              <Menu.Item onClick={() => setChangePasswordOpened(true)}>🔐 パスワード変更</Menu.Item>
              <Menu.Item
                component="a"
                href={MANUAL_PDF_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                📖 マニュアル
              </Menu.Item>
              <Menu.Item
                component="a"
                href={WEATHER_RISK_CRITERIA_PDF_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                📊 気象リスク判定基準
              </Menu.Item>
              {user?.role === Role.ADMIN && (
                <Menu.Item onClick={() => setDebugModalOpened(true)}>🛠 デバッグ</Menu.Item>
              )}
              <Menu.Item onClick={handleLogout}>🚪 ログアウト</Menu.Item>
            </Menu.Dropdown>
          </Menu>
        )}
        {!showInlineSidebar && (
          <ActionIcon
            variant="subtle"
            color="gray"
            onClick={closeDrawer}
            aria-label="サイドバーを閉じる"
          >
            <MaterialIcon icon="close" size="lg" />
          </ActionIcon>
        )}
      </Box>

      {/* 階層ツリー（ロール別） */}
      <Box style={{ flex: 1, overflow: 'auto', padding: layout.spacing.xs }}>
        {role === Role.ADMIN && renderAdminTree()}
        {role === Role.AREA_MANAGER && renderAreaManagerTree()}
        {role === Role.FARMER && renderFarmerTree()}
      </Box>

      <ChangePasswordModal
        opened={changePasswordOpened}
        onClose={() => setChangePasswordOpened(false)}
      />
      <DebugModal opened={debugModalOpened} onClose={() => setDebugModalOpened(false)} />
    </Box>
  );
}
